import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth  } from '../contexts/AuthContext';

const apiUrl = window._env_?.REACT_APP_API_URL || "https://app.mylocaleventrentals.com/api";

const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState("lastname");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const {uuid} = useParams();
  const navigate = useNavigate();
  const logout = useAuth();

  const token = localStorage.getItem("token");

  const fetchOrders = async () => {
    if (!token) {
      setError("Authentication required. Please log in.");
      logout();
      navigate('/login');
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${apiUrl}/order/customerorders/${uuid}`, {
        headers: { "x-auth-token": token },
      });
      if (res.data && Array.isArray(res.data)) {
        setCustomers(res.data);
        setTotalCount(res.data.totalCount || 0);
      } else {
        setCustomers(res.data);
        setTotalCount(res.data.length);
      }
    } catch (err) {
      console.error("Error fetching customers:", err);
      if (err.response?.status === 401) {
        setError("Session expired. Please log in again.");
      } else if (err.response?.status === 403) {
        setError("You don't have permission to view customers.");
      } else {
        setError("Failed to fetch customers. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [uuid]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleClick = (uuid) =>{
    navigate('/orders/'+uuid);
  }

  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phone;
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="ml-1 opacity-30">↕️</span>;
    return sortOrder === "asc" 
      ? <span className="ml-1">⬆️</span> 
      : <span className="ml-1">⬇️</span>;
  };

  return (
    <div className="p-4 md:p-6 my-2 md:my-6 bg-white shadow-lg rounded-lg">
      <h2 className="text-2xl md:text-3xl font-bold mb-6 text-gray-800">Customer Orders</h2>

      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="overflow-x-auto rounded-lg shadow">
                  <table className="min-w-full bg-white border border-gray-200">
                    <thead className="bg-gray-300">
                      <tr>
                        <th 
                          className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("firstname")}
                        >
                          <div className="flex items-center">
                            Order Number
                            <SortIcon field="firstname" />
                          </div>
                        </th>
                        <th 
                          className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("lastname")}
                        >
                          <div className="flex items-center">
                            Status
                            <SortIcon field="lastname" />
                          </div>
                        </th>
                        <th 
                          className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("email")}
                        >
                          <div className="flex items-center">
                            Start Date
                            <SortIcon field="email" />
                          </div>
                        </th>
                        <th 
                          className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer"
                          onClick={() => handleSort("phone")}
                        >
                          <div className="flex items-center">
                            End Date
                            <SortIcon field="phone" />
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {customers.map((customer) => (
                        <React.Fragment key={customer.uuid}>
                          <tr 
                            className={`hover:bg-gray-50 cursor-pointer ${selectedCustomer?.uuid === customer.uuid ? 'bg-blue-50' : ''}`}
                            onClick={() => handleClick(customer.uuid)}
                          >
                            <td className="px-4 py-3 whitespace-nowrap">{customer.formattedOrderNumber || "-"}</td>
                            <td className="px-4 py-3 whitespace-nowrap">{customer.status || "-"}</td>
                            <td className="px-4 py-3 whitespace-nowrap">{customer.startDate || "-"}</td>
                            <td className="px-4 py-3 whitespace-nowrap">{customer.endDate}</td>
                          </tr>
                          {selectedCustomer?.uuid === customer.uuid && (
                            <tr className="bg-gray-300 shadow-md">
                              <td colSpan="4" className="px-4 py-3">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <h3 className="font-medium text-gray-700 mb-2">Customer Details</h3>
                                    <p><span className="font-medium">Name:</span> {customer.firstname} {customer.lastname}</p>
                                    <p><span className="font-medium">Email:</span> {customer.email || "Not provided"}</p>
                                    <p><span className="font-medium">Phone:</span> {formatPhoneNumber(customer.phone) || "Not provided"}</p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
      </div>
    </div>
  );
};

export default CustomerList;